import { useState, useEffect } from 'react';
import { X, Upload, FileText, Download } from 'lucide-react';
import { bulkUploadUsers } from '../../../../api/apiFunction/userServices';
import { getAllPartners } from '../../../../api/apiFunction/partnerServices';
import { toast } from 'react-toastify';
import Papa from 'papaparse';

const BulkUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  
  // Form data for common fields
  const [formData, setFormData] = useState({
    accountType: 'sponsored',
    organization: '',
    partnerId: '',
    sendInvitation: false
  });

  // Sample CSV template
  const sampleCSV = `name,email,password
John Doe,john@example.com,Password123
Jane Smith,jane@example.com,Pass456`;

  const downloadSampleCSV = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'bulk_users_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Fetch approved partners when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchApprovedPartners();
    }
  }, [isOpen]);

  const fetchApprovedPartners = async () => {
    setPartnersLoading(true);
    try {
      const response = await getAllPartners(); // Get all partners
      if (response?.status === 200 && response?.data) {
        // Filter only non-expired partners
        const activePartners = response.data.filter(partner => !partner.isExpired);
        setPartners(activePartners);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setPartnersLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for organization dropdown
    if (name === 'organization') {
      const selectedPartner = partners.find(p => p._id === value);
      setFormData(prev => ({
        ...prev,
        organization: selectedPartner ? selectedPartner.name : '',
        partnerId: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length) {
      processFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length) {
      processFile(files[0]);
    }
  };

  const processFile = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'csv') {
      toast.error('Please upload a valid CSV file');
      return;
    }

    setFile(file);

    // Parse CSV for preview
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length) {
          toast.error('Error parsing CSV file');
          return;
        }

        // Validate required columns
        const headers = Object.keys(results.data[0] || {});
        const requiredColumns = ['name', 'email', 'password'];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));

        if (missingColumns.length > 0) {
          toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
          setFile(null);
          return;
        }

        // Show preview of first 5 rows
        setPreviewData(results.data.slice(0, 5));
      },
      error: (error) => {
        toast.error('Error parsing CSV file');
        console.error('CSV parse error:', error);
      },
    });
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setIsLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          // Prepare users data - apply common fields from form to all users
          const users = results.data.map(row => ({
            name: row.name?.trim() || '',
            email: row.email?.trim() || '',
            password: row.password?.trim() || '',
            accountType: formData.accountType,
            organization: formData.organization || '',
            partnerId: formData.partnerId || '',
            role: 'individual',
            sendInvitation: formData.sendInvitation
          })).filter(user => user.name && user.email && user.password);

          if (users.length === 0) {
            toast.error('No valid user data found in CSV');
            setIsLoading(false);
            return;
          }

          // Call bulk upload API
          const response = await bulkUploadUsers(users);

          if (response?.status === 200 || response?.status === 201) {
            const data = response.data;
            
            // Show success/failure toasts
            if (data.success?.length > 0) {
              toast.success(`Successfully created ${data.success.length} user(s)`);
            }

            if (data.failed?.length > 0) {
              toast.warning(`${data.failed.length} user(s) failed to create`);
            }

            // Close modal and refresh table
            handleClose();
            onUploadSuccess();
          } else {
            toast.error(response?.data?.message || 'Failed to upload users');
          }
        } catch (error) {
          console.error('Upload error:', error);
          toast.error('An error occurred during upload');
        } finally {
          setIsLoading(false);
        }
      },
      error: (error) => {
        toast.error('Error processing CSV file');
        console.error('CSV parse error:', error);
        setIsLoading(false);
      },
    });
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setFormData({
      accountType: 'sponsored',
      organization: '',
      partnerId: '',
      sendInvitation: false
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-bg-60 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-bd-50">
          <h2 className="text-xl font-semibold text-fg-90">Upload Users</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-bg-50 rounded-full transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Instructions */}
          <div className="text-sm text-fg-70 mb-4">
            Upload a CSV file with user details. Required columns: name, email, password.
            <button
              type="button"
              onClick={downloadSampleCSV}
              className="ml-2 text-ac-02 hover:text-ac-01 inline-flex items-center text-sm"
            >
              <Download size={14} className="mr-1" /> Download Sample CSV
            </button>
          </div>

          {/* Common Fields Form */}
          <div className="bg-bg-50/30 rounded-lg p-4 space-y-4 border border-bd-50">
            <div className="text-sm font-medium text-fg-60 mb-2">
              Apply to all users:
            </div>

            {/* Account Type */}
            <div>
              <label className="block text-sm font-medium text-fg-60 mb-1">
                Account Type
              </label>
              <select
                name="accountType"
                value={formData.accountType}
                onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
                disabled={isLoading}
              >
                <option value="sponsored">Sponsored</option>
                <option value="regular">Regular</option>
              </select>
            </div>

            {/* Organization - Only show for Sponsored accounts */}
            {formData.accountType === 'sponsored' && (
              <div>
                <label className="block text-sm font-medium text-fg-60 mb-1">
                  Organization (Partner)
                </label>
                <select
                  name="organization"
                  value={formData.partnerId}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 text-sm rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-2 focus:ring-ac-02"
                  disabled={partnersLoading || isLoading}
                >
                  <option value="">Select Partner Organization</option>
                  {partnersLoading ? (
                    <option value="">Loading partners...</option>
                  ) : (
                    partners.map((partner) => (
                      <option key={partner._id} value={partner._id}>
                        {partner.name}
                      </option>
                    ))
                  )}
                </select>
                {partners.length === 0 && !partnersLoading && (
                  <p className="text-xs text-fg-60 mt-1">No active partners available</p>
                )}
              </div>
            )}

            {/* Send Invitation */}
            <div className="flex items-center justify-between">
              <label className="text-sm text-fg-60">
                Send invitation email to users
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="sendInvitation"
                  checked={formData.sendInvitation}
                  onChange={handleFormChange}
                  className="sr-only peer"
                  disabled={isLoading}
                />
                <div className="w-11 h-6 bg-bg-40 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ac-02 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ac-02"></div>
              </label>
            </div>
          </div>

          {/* Upload Area */}
          {!file ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-ac-02 bg-ac-02/5'
                  : 'border-bd-50 hover:border-ac-02/50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('csv-upload-bulk').click()}
            >
              <div className="flex flex-col items-center justify-center space-y-2">
                <Upload size={24} className="text-fg-60" />
                <p className="text-sm text-fg-60">
                  {isDragging
                    ? 'Drop the file here'
                    : 'Drag & drop your CSV file here, or click to browse'}
                </p>
                <p className="text-xs text-fg-70">
                  Supports .csv files with name, email, password columns
                </p>
              </div>
              <input
                id="csv-upload-bulk"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex justify-between items-center p-3 bg-bg-50/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <FileText size={18} className="text-fg-60" />
                  <span className="text-sm font-medium text-fg-50">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setPreviewData([]);
                  }}
                  className="text-fg-60 hover:text-fg-50"
                  disabled={isLoading}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Preview */}
              {previewData.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-fg-60">
                    Preview (first {previewData.length} rows):
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-bd-50 text-sm">
                      <thead>
                        <tr className="bg-bg-50/30">
                          <th className="px-4 py-2 text-left text-xs font-medium text-fg-70 uppercase">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-fg-70 uppercase">Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-fg-70 uppercase">Password</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-bd-50">
                        {previewData.map((row, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 whitespace-nowrap text-fg-50">{row.name}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-fg-50">{row.email}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-fg-50">{'•'.repeat(8)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-fg-60 bg-bg-50 rounded-md hover:bg-bg-40 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            {file && (
              <button
                type="button"
                onClick={handleUpload}
                className="px-4 py-2 text-sm font-medium text-white bg-ac-02 rounded-md hover:bg-ac-01 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload Users
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
